import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { addTimeOffRequest, fetchLatestTimeOffRequestWOPFreezeEvent, isTOREventEligible } from "../api/timeOffRequest";
import { isUserAuthorizedByServer } from "../api/userAccess";
import AddTimeOffRequestDialog from "../components/AddTimeOffRequestDialog";
import PageLoading from "../components/PageLoading";
import TimeOffRequestTable from "../components/TimeOffRequestTable";
import TimeOffRequestTableMobile from "../components/TimeOffRequestTableMobile";
import ConfirmationDialog from "../components/fundamentals/ConfirmationDialog";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import WarningDialog from "../components/fundamentals/WarningDialog";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import {
  APPBAR_HEIGHT_PIXELS,
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  HOME_PATH,
  STATUS_SUBMITTED,
  TIME_OFF_ALL_WOP_TYPES,
  _4HOUR_VAC_TIME_OFF_TYPE,
} from "../util/constants";
import { setEndDateTime, setStartDateTime, validatePhoneNumber, validateStation } from "../util/inputValidation";
import {
  ARE_YOU_SURE_WANT_TO_SUBMIT_THE_TIME_OFF_REQUEST,
  ARE_YOU_SURE_WANT_TO_SUBMIT_THE_TIME_OFF_REQUESTS,
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  END_DATE_IS_REQUIRED,
  END_TIME_IS_REQUIRED,
  INVALID_PHONE_NUMBER,
  PHONE_REQUIRED,
  SHIFT_TIME_IS_REQUIRED,
  START_DATE_IS_REQUIRED,
  START_DATE_MUST_MATCH_END_DATE,
  START_TIME_IS_REQUIRED,
  TIME_OFF_CONFIRMATION_TITLE,
  TIME_OFF_ELIGIBILITY_EMPLOYEE_WARNING,
  TOR_MULTIPLE_REQUESTS_SUBMITTED_MESSAGE,
  TOR_SINGLE_REQUEST_SUBMITTED_MESSAGE,
} from "../util/messageConstants";
import { buildTimeOffRequestEventPayload } from "../util/timeOffRequestEventBuilder";
import {
  TOR_validateShiftEndDate,
  TOR_validateShiftEndTime,
  TOR_validateShiftOneHourDeadline,
  TOR_validateShiftStartDate,
  TOR_validateShiftStartTime,
  TOR_validateShiftStartTimePeriod,
  isDuplicateTimeOffRequest,
  validate24HourWindowForUnknownShiftTimes,
} from "../util/timeOffRequestInputValidation";
import {
  getWOPRequestDateTimes,
  isEADorEDUorSEAD,
  isLWOPTOWOPAllDayRequest,
  isLWOPTOWOPDayOfRequest,
  isLWOPTOWOPRequest,
  isTimeUnknownForShift,
  timeOffRequestDateTimeValidation,
  validateWOPDeadline,
  validateWOPPreferredLeaveTime,
} from "../util/timeOffRequestUtils";
import { getWorkgroupGivenJobCode, getUnionContractGivenJobCode } from "../util/workgroupMapper";
import {
  getEmployeeStation,
  getEmployeeStationList,
  getFormattedPhoneNumber,
  sortOnCaseSensitiveTexts,
} from "../util/commonUtils";
import StarsSelectInput from "../components/fundamentals/StarsSelectInput";
import { isDSTDay, TODAY, TOMORROW } from "../util/dateHelper";
import { DateTime } from "luxon";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "inherit",
    overflowY: "auto",
  },
  desktopRoot: {
    width: "80%",
    margin: "auto",
    paddingBottom: "100px",
  },
  mobileRoot: {
    paddingLeft: "10px",
    paddingRight: "10px",
    margin: "auto",
    overflowY: "scroll",
    height: `calc(100% - ${APPBAR_HEIGHT_PIXELS}px)`,
  },
  header: {
    float: "left",
    width: "20%",
  },
  subHeader: {
    padding: "20px",
    display: "flex",
    justifyContent: "space-between",
    "& .sub-title": {
      fontSize: "20px",
      color: theme.palette.secondary.swaGray,
    },
  },
  topDivider: {
    marginBottom: "20px !important",
  },
  bottomDivider: {
    marginTop: "20px !important",
  },

  // Mobile Version css
  pageTitle: {
    paddingBottom: "5px",
    marginLeft: "15px",
  },

  stationTextField: {
    width: "auto",
    marginTop: "12px",
    marginBottom: "12px",
  },

  subHeaderMobile: {
    display: "grid",
    paddingTop: "10px",
    "& .sub-title": {
      fontSize: "20px",
      color: theme.palette.secondary.swaGray,
      float: "left",
      position: "relative",
      display: "flex",
      paddingBottom: "10px",
      marginLeft: "15px",
    },
  },
  divider: {
    width: "100%",
  },
  pointer: {
    pointerEvents: "none",
  },

  button: {
    padding: "8px 22px",
    fontSize: "1.4rem",
    width: "95%",
    margin: "auto",
  },
  noTimeOffRequestMessage: {
    position: "relative",
    margin: "auto",
    top: "5vh",
    color: theme.palette.secondary.swaGray3,
    fontSize: "28px",
    height: "20vh",
  },
  actionButtons: {
    bottom: "0px",
    width: "100%",
  },
  areYouSureDialog: {
    zIndex: "9999 !important",
  },
}));

const EmployeeRequestTimeOff = (props) => {
  const classes = useStyles();
  const isAdminPage = false;
  const innerHtmlWidth = useMediaQuery({ maxWidth: 420 });
  const userSession = useSelector(selectUserSession);
  const userContract = getUnionContractGivenJobCode(userSession.jobCode);
  const [station, setStation] = useState("");
  const [isAreYouDialogOpen, toggleAreYouSureDialog] = useState(false);
  const [isAddTimeOffDialogOpen, setIsAddTimeOffDialogOpen] = useState(false);
  const [isEditTimeOffRequest, setIsEditTimeOffRequest] = useState(false);
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);
  const [isTimeOffRequestSubmitted, setIsTimeOffRequestSubmitted] = useState(false);
  const [isDSTWarningOpen, setIsDSTWarningOpen] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const timeOffRequestObj = useRef({});
  const [timeOffTypeError, setTimeOffTypeError] = useState("");
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [startTimeError, setStartTimeError] = useState("");
  const [endTimeError, setEndTimeError] = useState("");
  const [preferredWOPStartTimeError, setPreferredWOPStartTimeError] = useState("");
  const [preferredWOPEndTimeError, setPreferredWOPEndTimeError] = useState("");
  const [timeOffRequestsList, setTimeOffRequestsList] = useState([]);
  const [warningDialogBox, setWarningDialogBox] = useState(false);
  const [warningDialogBoxMessage, setWarningDialogBoxMessage] = useState("");
  let shiftStartTime = useRef({});
  let shiftEndTime = useRef({});
  let preferredWOPStartTime = useRef({});
  let preferredWOPEndTime = useRef({});
  let isWopListFrozen = useRef(false);
  const [wopFreezeMessage, setWopFreezeMessage] = useState("");
  const employeeWorkgroup = getWorkgroupGivenJobCode(userSession.jobCode);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [stationList, setStationList] = useState([]);
  const stationFilter = useSelector(selectStationFilter);
  const [stationErrors, setStationErrors] = useState("");
  const [asSoonAsPossibleChecked, setAsSoonAsPossibleChecked] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberErrors, setPhoneNumberErrors] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      setStation(getEmployeeStation(props.featureConfig.userStations, userSession, stationFilter));
      setStationList(getEmployeeStationList(userSession));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };

  const checkForEmptyDates = () => {
    if (!timeOffRequestObj.current.shiftStartDate || !timeOffRequestObj.current.shiftEndDate) {
      const startDateValidationMessage = TOR_validateShiftStartDate(
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      const endDateValidationMessage = TOR_validateShiftEndDate(
        null,
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      if (!!endDateValidationMessage || !!startDateValidationMessage) {
        return true;
      } else {
        return false;
      }
    }
  };

  const addTimeToDate = () => {
    if (
      !checkForEmptyDates() &&
      shiftStartTime.current["shiftStartHour"] !== undefined &&
      shiftStartTime.current["shiftStartMin"] !== undefined &&
      shiftEndTime.current["shiftEndHour"] !== undefined &&
      shiftEndTime.current["shiftEndMin"] !== undefined
    ) {
      let endTimeError = TOR_validateShiftEndTime(
        timeOffRequestObj.current.timeOffType,
        timeOffRequestObj.current["shiftStartDate"],
        timeOffRequestObj.current["shiftEndDate"],
        shiftStartTime.current["shiftStartHour"],
        shiftStartTime.current["shiftStartMin"],
        shiftEndTime.current["shiftEndHour"],
        shiftEndTime.current["shiftEndMin"],
        station
      );
      setEndTimeError(endTimeError);

      if (!endTimeError) {
        let timeDifferenceValidations = timeOffRequestDateTimeValidation(
          timeOffRequestObj.current["shiftStartDate"],
          timeOffRequestObj.current["shiftEndDate"],
          shiftStartTime.current["shiftStartHour"],
          shiftStartTime.current["shiftStartMin"],
          shiftEndTime.current["shiftEndHour"],
          shiftEndTime.current["shiftEndMin"],
          station,
          timeOffRequestObj.current.timeOffType,
          userContract
        );
        if (!!timeDifferenceValidations) {
          setEndTimeError(timeDifferenceValidations);
        } else {
          setEndTimeError("");
        }
      }
    }
  };

  const isValidTimeOff = () => {
    let isError = false;
    let deadLineErrors = "";
    let oneHourDeadLineError = "";
    let unknown24hrsShiftDateTimeError = "";
    let timeOffType = timeOffRequestObj.current["timeOffType"];
    let startDate = !!timeOffRequestObj.current["shiftStartDate"] ? timeOffRequestObj.current["shiftStartDate"] : null;
    let endDate = !!timeOffRequestObj.current["shiftEndDate"] ? timeOffRequestObj.current["shiftEndDate"] : null;
    let shiftStartHour =
      shiftStartTime.current["shiftStartHour"] !== undefined ? shiftStartTime.current["shiftStartHour"] : "";
    let shiftStartMin =
      shiftStartTime.current["shiftStartMin"] !== undefined ? shiftStartTime.current["shiftStartMin"] : "";

    let shiftEndHour = shiftEndTime.current["shiftEndHour"] !== undefined ? shiftEndTime.current["shiftEndHour"] : "";
    let shiftEndMin = shiftEndTime.current["shiftEndMin"] !== undefined ? shiftEndTime.current["shiftEndMin"] : "";

    let shiftStartDateError = TOR_validateShiftStartDate(startDate, station, timeOffType, userContract);
    let shiftEndDateError = TOR_validateShiftEndDate(startDate, endDate, station, timeOffType, userContract);
    let shiftStartTimeError = TOR_validateShiftStartTime(
      timeOffType,
      startDate,
      shiftStartHour,
      shiftStartMin,
      shiftEndHour,
      shiftEndMin,
      station
    );
    let shiftEndTimeError = TOR_validateShiftEndTime(
      timeOffType,
      startDate,
      endDate,
      shiftStartHour,
      shiftStartMin,
      shiftEndHour,
      shiftEndMin
    );
    if (
      startDate !== null &&
      endDate !== null &&
      shiftStartHour !== "" &&
      shiftStartMin !== "" &&
      shiftEndHour !== null &&
      shiftEndMin !== null
    ) {
      deadLineErrors = timeOffRequestDateTimeValidation(
        startDate,
        endDate,
        shiftStartHour,
        shiftStartMin,
        shiftEndHour,
        shiftEndMin,
        station,
        timeOffType,
        userContract
      );
      setEndTimeError(deadLineErrors);
    }
    oneHourDeadLineError = isLWOPTOWOPRequest(timeOffType)
      ? ""
      : TOR_validateShiftOneHourDeadline(startDate, shiftStartHour, shiftStartMin, station);
    if (!!oneHourDeadLineError) {
      setStartTimeError(oneHourDeadLineError);
    } else {
      setStartTimeError(oneHourDeadLineError);
    }

    if (!!shiftStartDateError) setStartDateError(shiftStartDateError);
    if (!!shiftEndDateError) setEndDateError(shiftEndDateError);
    if (!!shiftStartTimeError) setStartTimeError(shiftStartTimeError);
    if (!!shiftEndTimeError) setEndTimeError(shiftEndTimeError);

    if (
      isTimeUnknownForShift(shiftStartHour, shiftStartMin, shiftEndHour, shiftEndMin) &&
      isEADorEDUorSEAD(timeOffRequestObj.current.timeOffType)
    ) {
      unknown24hrsShiftDateTimeError = validate24HourWindowForUnknownShiftTimes(startDate, endDate, station);
      if (unknown24hrsShiftDateTimeError === SHIFT_TIME_IS_REQUIRED) {
        setEndTimeError(unknown24hrsShiftDateTimeError);
        setStartTimeError(unknown24hrsShiftDateTimeError);
      } else if (unknown24hrsShiftDateTimeError === START_DATE_MUST_MATCH_END_DATE) {
        setEndDateError(unknown24hrsShiftDateTimeError);
      }
    }

    if (isLWOPTOWOPRequest(timeOffType)) {
      if (phoneNumber === "") {
        setPhoneNumberErrors(PHONE_REQUIRED);
        isError = true;
      } else if (phoneNumberErrors !== "") {
        isError = true;
      }

      if (!(isLWOPTOWOPDayOfRequest(timeOffType) && asSoonAsPossibleChecked)) {
        if (
          preferredWOPStartTime.current.preferredWOPStartHour === undefined ||
          preferredWOPStartTime.current.preferredWOPStartMin === undefined
        ) {
          setPreferredWOPStartTimeError(START_TIME_IS_REQUIRED);
          isError = true;
        }
        if (
          preferredWOPEndTime.current.preferredWOPEndHour === undefined ||
          preferredWOPEndTime.current.preferredWOPEndMin === undefined
        ) {
          setPreferredWOPEndTimeError(END_TIME_IS_REQUIRED);
          isError = true;
        }

        if (shiftStartHour === "" || shiftStartMin === "" || shiftEndHour === "" || shiftEndMin === "") {
          isError = true;
        }

        if (!isError && !isLWOPTOWOPAllDayRequest(timeOffRequestObj.current.timeOffType)) {
          startDate = startDate.set({ hour: shiftStartHour, minute: shiftStartMin });
          endDate = endDate.set({ hour: shiftEndHour, minute: shiftEndMin });
          const error = validateWOPPreferredLeaveTime(
            startDate,
            endDate,
            preferredWOPStartTime.current,
            preferredWOPEndTime.current,
            asSoonAsPossibleChecked,
            station,
            isAdminPage
          );
          setPreferredWOPStartTimeError(error);
          setPreferredWOPEndTimeError(error);

          if (error !== "") {
            isError = true;
          }
        }
      }
    }

    if (
      !!shiftStartDateError ||
      !!shiftEndDateError ||
      !!shiftStartTimeError ||
      !!shiftEndTimeError ||
      !!deadLineErrors ||
      !!oneHourDeadLineError ||
      !!unknown24hrsShiftDateTimeError
    ) {
      isError = true;
      return isError;
    } else {
      return isError;
    }
  };

  const selectTimeOffRequestTimeOff = (selectedTimeOffType) => {
    timeOffRequestObj.current["timeOffType"] = selectedTimeOffType.target.value;

    if (selectedTimeOffType.target.value === _4HOUR_VAC_TIME_OFF_TYPE) {
      setTimeFor4HrVacTimeOffType(shiftStartTime.current["shiftStartHour"]);
    }

    if (isLWOPTOWOPRequest(selectedTimeOffType.target.value)) {
      setDateForWOPTimeOffType(selectedTimeOffType.target.value);
    }
    forceUpdate();
    setStartDateError("");
    setEndDateError("");
    setStartTimeError("");
    setEndTimeError("");
    setTimeOffTypeError("");
    setPreferredWOPStartTimeError("");
    setPreferredWOPEndTimeError("");
    setPhoneNumberErrors("");
  };

  const getAutomatedEndDateAndEndHourValueFor4HrVacTimeOffType = (hrInput) => {
    const VAC_SHIFT_TIME_HOURS = 4;
    let automatedDateValue;
    let automatedHourValue = hrInput + VAC_SHIFT_TIME_HOURS;

    // Crossing midnight handler
    if (automatedHourValue > 23) {
      let remainingHours = hrInput + VAC_SHIFT_TIME_HOURS - 23;
      automatedHourValue = remainingHours - 1;

      if (timeOffRequestObj.current.shiftStartDate !== undefined) {
        automatedDateValue = timeOffRequestObj.current.shiftStartDate.plus({ days: 1 });
      }

      return [automatedDateValue, automatedHourValue];
    }

    return [timeOffRequestObj.current.shiftStartDate, automatedHourValue];
  };

  const handleShiftStartDateChange = (selectedStartDate) => {
    setValidationError("", "");
    const _timeOffRequestObj = { ...timeOffRequestObj.current };
    _timeOffRequestObj["shiftStartDate"] = selectedStartDate;
    _timeOffRequestObj["shiftEndDate"] = selectedStartDate;

    // Logic to set the end date for LWOP/TOWOP to allowed day if start date is in the past
    if (isLWOPTOWOPRequest(timeOffRequestObj.current.timeOffType)) {
      if (isLWOPTOWOPAllDayRequest(timeOffRequestObj.current.timeOffType) && selectedStartDate < TODAY(station)) {
        _timeOffRequestObj["shiftEndDate"] = selectedStartDate.plus({ days: 1 });
      } else if (selectedStartDate <= TODAY(station).plus({ days: -1 }))
        _timeOffRequestObj["shiftEndDate"] = selectedStartDate.plus({ days: 1 });
    }

    timeOffRequestObj.current = _timeOffRequestObj;
    forceUpdate();
    let startDateValidationMessage = TOR_validateShiftStartDate(
      timeOffRequestObj.current.shiftStartDate,
      station,
      timeOffRequestObj.current.timeOffType,
      userContract
    );

    if (!!startDateValidationMessage) {
      setEndDateError(startDateValidationMessage);
    } else {
      setEndDateError("");
    }

    if (startDateError === START_DATE_IS_REQUIRED) {
      setStartDateError("");
    }

    if (
      shiftStartTime.current["shiftStartHour"] !== undefined &&
      shiftStartTime.current["shiftStartMin"] !== undefined &&
      startTimeError === START_TIME_IS_REQUIRED
    ) {
      setStartTimeError("");
    }

    if (
      shiftStartTime.current["shiftStartHour"] !== undefined &&
      shiftStartTime.current["shiftStartMin"] !== undefined
    ) {
      setStartTimeError(
        checkPastDateTime(shiftStartTime.current["shiftStartHour"], shiftStartTime.current["shiftStartMin"])
      );
    }

    if (
      timeOffRequestObj.current["shiftStartDate"] &&
      timeOffRequestObj.current["shiftEndDate"] &&
      (isDSTDay(timeOffRequestObj.current["shiftStartDate"]) || isDSTDay(timeOffRequestObj.current["shiftEndDate"]))
    ) {
      setIsDSTWarningOpen(true);
    } else {
      setIsDSTWarningOpen(false);
    }

    addTimeToDate();
  };

  const handleShiftStartDateHoursChange = (selectedStartHour) => {
    setValidationError("", "");
    let shiftMin = shiftStartTime.current.shiftStartMin !== undefined ? shiftStartTime.current.shiftStartMin : "";
    shiftStartTime.current["shiftStartHour"] = selectedStartHour.target.value;

    if (timeOffRequestObj.current.timeOffType === _4HOUR_VAC_TIME_OFF_TYPE) {
      setTimeFor4HrVacTimeOffType(selectedStartHour.target.value);
    }

    if (!timeOffRequestObj.current.shiftStartDate) {
      let startDateValidationMessage = TOR_validateShiftStartDate(
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      setStartDateError(startDateValidationMessage);
    }

    let startDateErrors = checkPastDateTime(shiftStartTime.current["shiftStartHour"], shiftMin);
    setStartTimeError(startDateErrors);
    if (!!timeOffRequestObj.current.shiftStartDate && shiftMin !== "" && startDateErrors === "") {
      let oneHourDeadLineError = isLWOPTOWOPRequest(timeOffRequestObj.current.timeOffType)
        ? ""
        : TOR_validateShiftOneHourDeadline(
            timeOffRequestObj.current.shiftStartDate,
            shiftStartTime.current["shiftStartHour"],
            shiftMin,
            station
          );
      if (!!oneHourDeadLineError) {
        setStartTimeError(oneHourDeadLineError);
      } else {
        setStartTimeError(oneHourDeadLineError);
      }
    }
    addTimeToDate();
    forceUpdate();
  };

  const setTimeFor4HrVacTimeOffType = (shiftStartHour) => {
    let shiftEndDateAndHourList = getAutomatedEndDateAndEndHourValueFor4HrVacTimeOffType(shiftStartHour);
    if (shiftEndDateAndHourList[0] !== undefined) {
      timeOffRequestObj.current["shiftEndDate"] = shiftEndDateAndHourList[0];
    }
    shiftEndTime.current["shiftEndHour"] = !!shiftEndDateAndHourList[1] ? shiftEndDateAndHourList[1] : undefined;
  };

  const handleShiftStartDateMinChange = (selectedStartMin) => {
    setValidationError("", "");
    shiftStartTime.current["shiftStartMin"] = selectedStartMin.target.value;

    // UX for 4-Hour VAC input
    if (timeOffRequestObj.current.timeOffType === _4HOUR_VAC_TIME_OFF_TYPE) {
      shiftEndTime.current["shiftEndMin"] = selectedStartMin.target.value;
    }

    let shiftHour = shiftStartTime.current.shiftStartHour !== undefined ? shiftStartTime.current.shiftStartHour : "";
    if (!timeOffRequestObj.current.shiftStartDate) {
      let startDateValidationMessage = TOR_validateShiftStartDate(
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      setStartDateError(startDateValidationMessage);
    }
    let startTimeErrors = checkPastDateTime(shiftHour, shiftStartTime.current["shiftStartMin"]);
    setStartTimeError(startTimeErrors);
    if (!!timeOffRequestObj.current.shiftStartDate && shiftHour !== "" && startTimeErrors === "") {
      let oneHourDeadLineError = isLWOPTOWOPRequest(timeOffRequestObj.current.timeOffType)
        ? ""
        : TOR_validateShiftOneHourDeadline(
            timeOffRequestObj.current.shiftStartDate,
            shiftHour,
            shiftStartTime.current["shiftStartMin"],
            station
          );
      if (!!oneHourDeadLineError) {
        setStartTimeError(oneHourDeadLineError);
      } else {
        setStartTimeError(oneHourDeadLineError);
      }
    }
    addTimeToDate();
    forceUpdate();
  };

  const handleShiftEndDateChange = (selectedEndDate) => {
    setValidationError("", "");
    let endDateValidation = "";
    const _timeOffRequestObj = { ...timeOffRequestObj.current };
    _timeOffRequestObj["shiftEndDate"] = selectedEndDate;
    timeOffRequestObj.current = _timeOffRequestObj;
    forceUpdate();
    if (!!timeOffRequestObj.current.shiftStartDate) {
      endDateValidation = TOR_validateShiftEndDate(
        timeOffRequestObj.current.shiftStartDate,
        timeOffRequestObj.current.shiftEndDate,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      setEndDateError(endDateValidation);
    }

    if (!timeOffRequestObj.current.shiftStartDate) {
      let startDateError = TOR_validateShiftStartDate(
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      setStartDateError(startDateError);
      setEndDateError("");
    }

    if (
      timeOffRequestObj.current["shiftStartDate"] &&
      timeOffRequestObj.current["shiftEndDate"] &&
      (isDSTDay(timeOffRequestObj.current["shiftStartDate"]) || isDSTDay(timeOffRequestObj.current["shiftEndDate"]))
    ) {
      setIsDSTWarningOpen(true);
    } else {
      setIsDSTWarningOpen(false);
    }

    addTimeToDate();
  };

  const handleShiftEndDateHoursChange = (selectedEndHour) => {
    setValidationError("", "");
    shiftEndTime.current["shiftEndHour"] = selectedEndHour.target.value;
    if (!!checkForEmptyDates()) {
      if (!timeOffRequestObj.current.shiftStartDate) {
        setStartDateError(START_DATE_IS_REQUIRED);
      }
      if (!timeOffRequestObj.current.shiftEndDate) {
        setEndDateError(END_DATE_IS_REQUIRED);
      }
    }
    if (shiftEndTime.current["shiftEndMin"] !== undefined) {
      setEndTimeError("");
    }
    addTimeToDate();
    forceUpdate();
  };

  const handleShiftEndDateMinChange = (selectedEndMin) => {
    setValidationError("", "");
    shiftEndTime.current["shiftEndMin"] = selectedEndMin.target.value;
    if (!!checkForEmptyDates()) {
      if (!timeOffRequestObj.current.shiftStartDate) {
        setStartDateError(START_DATE_IS_REQUIRED);
      }
      if (!timeOffRequestObj.current.shiftEndDate) {
        setEndDateError(END_DATE_IS_REQUIRED);
      }
    }
    if (shiftEndTime.current["shiftEndHour"] !== undefined) {
      setEndTimeError("");
    }
    addTimeToDate();
    forceUpdate();
  };

  const handlePreferredWOPStartHourChange = (selectedStartHour) => {
    setValidationError("", "");
    setPreferredWOPStartTimeError("");
    preferredWOPStartTime.current.preferredWOPStartHour = selectedStartHour.target.value;

    if (preferredWOPStartTime.current.preferredWOPStartMin) {
      setPreferredWOPStartTimeError("");
    }

    if (
      preferredWOPEndTime.current.preferredWOPEndHour !== undefined &&
      preferredWOPEndTime.current.preferredWOPEndMin !== undefined
    ) {
      setPreferredWOPEndTimeError("");
    }

    forceUpdate();
  };

  const handlePreferredWOPStartMinChange = (selectedStartMin) => {
    setValidationError("", "");
    setPreferredWOPStartTimeError("");
    preferredWOPStartTime.current.preferredWOPStartMin = selectedStartMin.target.value;

    if (
      !preferredWOPStartTime.current.preferredWOPStartHour &&
      preferredWOPStartTime.current.preferredWOPStartHour !== 0
    ) {
      setPreferredWOPStartTimeError(START_TIME_IS_REQUIRED);
    }

    if (
      preferredWOPEndTime.current.preferredWOPEndHour !== undefined &&
      preferredWOPEndTime.current.preferredWOPEndMin !== undefined
    ) {
      setPreferredWOPEndTimeError("");
    }

    forceUpdate();
  };

  const handlePreferredWOPEndHourChange = (selectedEndHour) => {
    setValidationError("", "");
    setPreferredWOPEndTimeError("");
    preferredWOPEndTime.current.preferredWOPEndHour = selectedEndHour.target.value;

    if (preferredWOPEndTime.current.preferredWOPEndMin) {
      setPreferredWOPEndTimeError("");
    }

    if (
      preferredWOPStartTime.current.preferredWOPStartHour !== undefined &&
      preferredWOPStartTime.current.preferredWOPStartMin !== undefined
    ) {
      setPreferredWOPStartTimeError("");
    }

    forceUpdate();
  };

  const handlePreferredWOPEndMinChange = (selectedEndMin) => {
    setValidationError("", "");
    setPreferredWOPEndTimeError("");
    preferredWOPEndTime.current.preferredWOPEndMin = selectedEndMin.target.value;

    if (!preferredWOPEndTime.current.preferredWOPEndHour && preferredWOPEndTime.current.preferredWOPEndHour !== 0) {
      setPreferredWOPEndTimeError(END_TIME_IS_REQUIRED);
    }

    if (
      preferredWOPStartTime.current.preferredWOPStartHour !== undefined &&
      preferredWOPStartTime.current.preferredWOPStartMin !== undefined
    ) {
      setPreferredWOPStartTimeError("");
    }

    forceUpdate();
  };

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };

  const handlePhoneNumberBlur = () => {
    validateContactNumber();
  };

  const validateContactNumber = () => {
    const phoneNumberValidation = validatePhoneNumber(phoneNumber, PHONE_REQUIRED, INVALID_PHONE_NUMBER);
    setPhoneNumberErrors(phoneNumberValidation);
    if (phoneNumberValidation === "") {
      const formattedPhoneNumber = getFormattedPhoneNumber(phoneNumber);
      setPhoneNumber(formattedPhoneNumber);
    }
  };

  const handleAsSoonAsPossibleChecked = (event) => {
    setAsSoonAsPossibleChecked(event.target.checked);
    if (event.target.checked) {
      setPreferredWOPStartTimeError("");
      setPreferredWOPEndTimeError("");
      preferredWOPStartTime.current.preferredWOPStartHour = 0;
      preferredWOPStartTime.current.preferredWOPStartMin = 0;
      preferredWOPEndTime.current.preferredWOPEndHour = 0;
      preferredWOPEndTime.current.preferredWOPEndMin = 0;
    }
  };

  const setDateForWOPTimeOffType = (timeOffType) => {
    const dateToSet = isLWOPTOWOPDayOfRequest(timeOffType) ? TODAY(station) : TOMORROW(station);

    timeOffRequestObj.current["shiftEndDate"] = dateToSet;
    timeOffRequestObj.current["shiftStartDate"] = dateToSet;

    if (isLWOPTOWOPAllDayRequest(timeOffType)) {
      preferredWOPStartTime.current.preferredWOPStartHour = 0;
      preferredWOPStartTime.current.preferredWOPStartMin = 0;
      preferredWOPEndTime.current.preferredWOPEndHour = 0;
      preferredWOPEndTime.current.preferredWOPEndMin = 0;
    }
    addTimeToDate();
  };

  const checkPastDateTime = (shiftHour, shiftMin) => {
    let startDateTimeValidationMessage = "";
    if (!!timeOffRequestObj.current.shiftStartDate && shiftHour !== "" && shiftMin !== "") {
      startDateTimeValidationMessage = TOR_validateShiftStartTimePeriod(
        /** Shift Time cannot be in the past */
        station,
        timeOffRequestObj.current.shiftStartDate,
        shiftHour,
        shiftMin,
        timeOffRequestObj.current.timeOffType
      );
    }
    return startDateTimeValidationMessage;
  };

  const sortRequest = (list) => {
    list.sort((a, b) => a.shiftStartDateTime.ts - b.shiftStartDateTime.ts);
    list.sort((a, b) =>
      a.shiftStartDateTime.ts === b.shiftStartDateTime.ts ? a.timeOffType.localeCompare(b.timeOffType) : 1
    );
    return list;
  };

  const handleOnSave = () => {
    const isExpired = false;
    const shiftExistsError = "Shift has already been added";
    if (!!timeOffRequestObj.current.timeOffType) {
      if (!isValidTimeOff()) {
        let timeOffRequest = {};
        let _timeOffRequestsList = [...timeOffRequestsList];
        const timeOffType = timeOffRequestObj.current["timeOffType"];
        const shiftStartDateTime = setStartDateTime(
          timeOffRequestObj.current["shiftStartDate"],
          shiftStartTime.current["shiftStartHour"],
          shiftStartTime.current["shiftStartMin"],
          station
        );
        const shiftEndDateTime = setEndDateTime(
          timeOffRequestObj.current["shiftEndDate"],
          shiftEndTime.current["shiftEndHour"],
          shiftEndTime.current["shiftEndMin"],
          station
        );

        const addWOPDataToTOR = () => {
          if (isLWOPTOWOPAllDayRequest(timeOffType)) {
            timeOffRequest.preferredWOPStartTime = shiftStartDateTime.toISO();
            timeOffRequest.preferredWOPEndTime = shiftEndDateTime.toISO();
          } else {
            const requestDateTimes = getWOPRequestDateTimes(
              shiftStartDateTime,
              shiftEndDateTime,
              preferredWOPStartTime.current.preferredWOPStartHour,
              preferredWOPStartTime.current.preferredWOPStartMin,
              preferredWOPEndTime.current.preferredWOPEndHour,
              preferredWOPEndTime.current.preferredWOPEndMin,
              asSoonAsPossibleChecked
            );
            timeOffRequest.preferredWOPStartTime = requestDateTimes.requestStart.toISO();
            timeOffRequest.preferredWOPEndTime = requestDateTimes.requestEnd.toISO();
          }
          timeOffRequest.wopASAP = asSoonAsPossibleChecked;
          timeOffRequest.phoneNumber = phoneNumber;
        };

        if (!timeOffRequestObj.current["id"]) {
          timeOffRequest = {
            id: Math.random().toString(36).slice(2),
            timeOffType,
            shiftStartDateTime,
            shiftEndDateTime,
            isExpired,
          };
          if (isLWOPTOWOPRequest(timeOffType)) {
            addWOPDataToTOR();
          }
          if (isDuplicateTimeOffRequest(_timeOffRequestsList, timeOffRequest)) {
            setValidationError(shiftExistsError, "");
          } else {
            _timeOffRequestsList.push(timeOffRequest);
            const sortedRequestList = sortRequest(_timeOffRequestsList);
            setTimeOffRequestsList(sortedRequestList);
            setIsAddTimeOffDialogOpen(false);
            handleCloseAddRequestModal();
          }
        } else {
          const id = timeOffRequestObj.current["id"];
          timeOffRequest = { id, timeOffType, shiftStartDateTime, shiftEndDateTime, isExpired };
          if (isLWOPTOWOPRequest(timeOffType)) {
            addWOPDataToTOR();
          }
          if (isDuplicateTimeOffRequest(_timeOffRequestsList, timeOffRequest)) {
            setValidationError(shiftExistsError, "");
          } else {
            const updatedTimeOffRequest = _timeOffRequestsList.map((timeOfRequest) =>
              timeOfRequest.id === timeOffRequestObj.current["id"]
                ? { ...timeOfRequest, ...timeOffRequest }
                : timeOfRequest
            );
            const sortedRequestList = sortRequest(updatedTimeOffRequest);
            setTimeOffRequestsList(sortedRequestList);
            handleCloseAddRequestModal();
          }
        }
      }
    } else {
      setTimeOffTypeError("Time Off Type is required");
    }
  };

  const setValidationError = (message, replacementMessage) => {
    setStartDateError(replacementMessage);
    setStartTimeError(replacementMessage);
    setEndDateError(replacementMessage);
    setEndTimeError(message);
  };

  const handleOnEditTimeOffRequest = (timeOffRequest) => {
    console.log("handleOnEditTimeOffRequest");
    setIsEditTimeOffRequest(true);
    timeOffRequestObj.current["id"] = timeOffRequest.id;
    timeOffRequestObj.current["timeOffType"] = timeOffRequest.timeOffType;
    timeOffRequestObj.current["shiftStartDate"] = timeOffRequest.shiftStartDateTime;
    timeOffRequestObj.current["shiftEndDate"] = timeOffRequest.shiftEndDateTime;
    shiftStartTime.current["shiftStartHour"] = timeOffRequest.shiftStartDateTime.hour;
    shiftStartTime.current["shiftStartMin"] = timeOffRequest.shiftStartDateTime.minute;
    shiftEndTime.current["shiftEndHour"] = timeOffRequest.shiftEndDateTime.hour;
    shiftEndTime.current["shiftEndMin"] = timeOffRequest.shiftEndDateTime.minute;
    if (isLWOPTOWOPRequest(timeOffRequest.timeOffType)) {
      preferredWOPStartTime.current["preferredWOPStartHour"] = DateTime.fromISO(timeOffRequest.preferredWOPStartTime, {
        setZone: true,
      }).hour;
      preferredWOPStartTime.current["preferredWOPStartMin"] = DateTime.fromISO(timeOffRequest.preferredWOPStartTime, {
        setZone: true,
      }).minute;
      preferredWOPEndTime.current["preferredWOPEndHour"] = DateTime.fromISO(timeOffRequest.preferredWOPEndTime, {
        setZone: true,
      }).hour;
      preferredWOPEndTime.current["preferredWOPEndMin"] = DateTime.fromISO(timeOffRequest.preferredWOPEndTime, {
        setZone: true,
      }).minute;
      setAsSoonAsPossibleChecked(timeOffRequest.wopASAP);
      setPhoneNumber(timeOffRequest.phoneNumber);
    }
  };

  const handleCloseAddRequestModal = () => {
    setIsAddTimeOffDialogOpen(false);
    timeOffRequestObj.current = {};
    shiftStartTime.current = {};
    shiftEndTime.current = {};
    preferredWOPStartTime.current = {};
    preferredWOPEndTime.current = {};
    setPreferredWOPStartTimeError("");
    setPreferredWOPEndTimeError("");
    setAsSoonAsPossibleChecked(false);
    setPhoneNumber("");
    setPhoneNumberErrors("");
    setStartDateError("");
    setEndDateError("");
    setStartTimeError("");
    setEndTimeError("");
    setTimeOffTypeError("");
    setIsDSTWarningOpen(false);
    setIsEditTimeOffRequest(false);
  };

  const handleOnRemove = () => {
    let trimmedTimeOffRequests = timeOffRequestsList.filter(
      (timeOffRequest) => timeOffRequest.id !== timeOffRequestObj.current.id
    );
    setTimeOffRequestsList(trimmedTimeOffRequests);
    handleCloseAddRequestModal();
    toggleAreYouSureDialog(false);
  };

  const handleOnYesButtonClick = () => {
    if (isEditTimeOffRequest) {
      handleOnRemove();
    }
    if (isSubmitButtonClicked && handleOnSubmitValidation()) {
      handleOnSubmit();
    } else {
      toggleAreYouSureDialogBox(false);
    }
  };

  const buildWOPObject = (timeOffRequest) => {
    return {
      preferredWOPStart: timeOffRequest.preferredWOPStartTime,
      preferredWOPEnd: timeOffRequest.preferredWOPEndTime,
      wopASAP: timeOffRequest.wopASAP,
      phoneNumber: timeOffRequest.phoneNumber,
    };
  };

  const prepareTimeOffRequestObject = () => {
    const getConvertedTimeOffRequests = () => {
      let timeOffRequestList = [];
      for (let timeOffRequest of timeOffRequestsList) {
        let timeOffRequestObj = {};
        timeOffRequestObj.timeOffRequestType = timeOffRequest.timeOffType;
        timeOffRequestObj.shiftStart = timeOffRequest.shiftStartDateTime;
        timeOffRequestObj.shiftEnd = timeOffRequest.shiftEndDateTime;
        if (isLWOPTOWOPRequest(timeOffRequest.timeOffType)) {
          timeOffRequestObj = {
            ...timeOffRequestObj,
            ...buildWOPObject(timeOffRequest),
          };
        }
        timeOffRequestList.push(timeOffRequestObj);
      }
      return timeOffRequestList;
    };

    const timeOffRequestObj = {
      station: station,
      workgroup: employeeWorkgroup,
      empId: userSession.eid,
      empFirstName: userSession.firstName,
      empLastName: userSession.lastName,
      empWorkgroup: employeeWorkgroup,
      isAdditionalApproved: false,
      isReinstated: false,
      isAdminCreated: false,
      requestingShifts: getConvertedTimeOffRequests(),
    };

    return timeOffRequestObj;
  };

  const handleOnSubmit = async () => {
    setIsTimeOffRequestSubmitted(true);

    const timeOffRequestObject = prepareTimeOffRequestObject();

    const timeOffRequestPayload = buildTimeOffRequestEventPayload(STATUS_SUBMITTED, timeOffRequestObject, userSession);

    const isTorEligible = await isTOREventEligible(timeOffRequestPayload);

    if (!isTorEligible) {
      setWarningDialogBox(true);
      const errorMessage = TIME_OFF_ELIGIBILITY_EMPLOYEE_WARNING;
      setWarningDialogBoxMessage(errorMessage);
      toggleAreYouSureDialogBox(false);
      setIsTimeOffRequestSubmitted(false);
    } else {
      setIsTimeOffRequestSubmitted(true);
      addTimeOffRequest(timeOffRequestPayload)
        .then((res) => res.json())
        .then((result) => {
          toggleAreYouSureDialogBox(false);
          setOpenConfirmationDialog(true);
          setIsTimeOffRequestSubmitted(false);
        })
        .catch((err) => {
          toggleAreYouSureDialogBox(false);
          setIsTimeOffRequestSubmitted(false);
          console.error("Exception adding Time Off Request", err);
        });
    }
  };

  const toggleAreYouSureDialogBox = (flag) => {
    toggleAreYouSureDialog(flag);
    setIsSubmitButtonClicked(flag);
    setIsTimeOffRequestSubmitted(flag);
  };

  const getConfirmationMessage = () => {
    if (isEditTimeOffRequest) {
      return "Are you sure you want to remove the shift?";
    }
    if (isSubmitButtonClicked) {
      if (timeOffRequestsList?.length > 1) {
        return ARE_YOU_SURE_WANT_TO_SUBMIT_THE_TIME_OFF_REQUESTS;
      } else {
        return ARE_YOU_SURE_WANT_TO_SUBMIT_THE_TIME_OFF_REQUEST;
      }
    } else {
      return isEditTimeOffRequest
        ? ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS)
        : ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA);
    }
  };

  const handleNoClickedCancelDialog = () => {
    toggleAreYouSureDialog(false);
    setIsSubmitButtonClicked(false);
  };

  const renderCancelAreYouSureDialog = () => {
    return (
      <>
        {!isEditTimeOffRequest && !isSubmitButtonClicked && (
          <ConfirmationDialog
            isDialogOpen={isAreYouDialogOpen}
            message={getConfirmationMessage()}
            handleNoButtonClicked={handleNoClickedCancelDialog}
            redirectTo={props.featureConfig.path}
          />
        )}
        {(isEditTimeOffRequest || isSubmitButtonClicked) && (
          <ConfirmationDialog
            isDialogOpen={isAreYouDialogOpen}
            message={getConfirmationMessage()}
            hasInfo={isWopListFrozen.current}
            infoMessage={wopFreezeMessage}
            handleNoButtonClicked={handleNoClickedCancelDialog}
            handleYesButtonClicked={handleOnYesButtonClick}
            isYesLoading={isTimeOffRequestSubmitted}
          />
        )}
      </>
    );
  };
  const handleWarningDialogClose = () => {
    setWarningDialogBox(false);
    setWarningDialogBoxMessage("");
  };
  const noTimeOffRequestMessage = () => {
    return <div className={classes.noTimeOffRequestMessage}>No Time Off Requested</div>;
  };

  const handleOnSubmitValidation = () => {
    let flag = true;
    const response = timeOffRequestsList?.map((ele) => {
      let status = "";
      if (isLWOPTOWOPRequest(ele?.timeOffType)) {
        const preferredWOPStartTime = DateTime.fromISO(ele?.preferredWOPStartTime, { setZone: true });
        const preferredWOPEndTime = DateTime.fromISO(ele?.preferredWOPEndTime, { setZone: true });
        const preferredReleaseStart = {
          preferredWOPStartHour: preferredWOPStartTime.hour,
          preferredWOPStartMin: preferredWOPStartTime.minute,
        };
        const preferredReleaseEnd = {
          preferredWOPEndHour: preferredWOPEndTime.hour,
          preferredWOPEndMin: preferredWOPEndTime.minute,
        };
        status = validateWOPDeadline(
          ele?.shiftStartDateTime,
          ele?.shiftEndDateTime,
          preferredReleaseStart,
          preferredReleaseEnd,
          ele?.wopASAP,
          station,
          ele?.timeOffType
        );
      } else {
        status = TOR_validateShiftOneHourDeadline(
          ele?.shiftStartDateTime,
          ele?.shiftStartDateTime.hour,
          ele?.shiftStartDateTime.minute,
          station
        );
      }

      if (status) {
        ele.isExpired = true;
      } else {
        ele.isExpired = false;
      }
      return status;
    });
    setTimeOffRequestsList(timeOffRequestsList);

    const expiredList = response?.filter((ele) => ele !== "");
    if (expiredList?.length === 1 && timeOffRequestsList?.length === 1) {
      handleWarningDialogBox("Shift is past the submission deadline!");
      flag = false;
    } else if (timeOffRequestsList?.length > 1 && expiredList?.length > 0) {
      handleWarningDialogBox("At least 1 shift is past the submission deadline!");
      flag = false;
    }
    return flag;
  };

  const handleWarningDialogBox = (message) => {
    setWarningDialogBox(!warningDialogBox);
    setWarningDialogBoxMessage(message);
  };

  const handleStationChange = (event) => {
    dispatch(updateStationFilter(event.target.value));
    setStation(event.target.value);
  };

  const handleStationBlur = (event) => {
    setStationErrors(validateStation(event.target.value));
  };

  const breadcrumbsList = [BREADCRUMBS_TITLE.HOME, BREADCRUMBS_TITLE.MY_TIME_OFF, BREADCRUMBS_TITLE.REQUEST_TIME_OFF];
  const breadcrumbsLinks = [HOME_PATH, props.featureConfig.path];
  const confirmationRequired = () => {
    if (timeOffRequestsList.length > 0) {
      return true;
    } else {
      return false;
    }
  };

  const setIsWopListFrozen = async (timeOffRequestsList) => {
    const buildWopFreezeMessage = (frozenTimeOffTypes, frozenRequestCount) => {
      const uniqueTypes = [...new Set(frozenTimeOffTypes)];
      const frozenTypesString = uniqueTypes.join(" and ");
      const isTypePlural = uniqueTypes.length > 1;
      const isRequestPlural = frozenRequestCount > 1;
      return `Award is in progress for the ${frozenTypesString} list${isTypePlural ? "s" : ""}. Your request${
        isRequestPlural ? "s" : ""
      } will be considered during the next award.`;
    };

    let frozenWopTypes = [];
    let frozenRequestCount = 0;
    let isFrozen = false;
    for (const timeOffRequest of timeOffRequestsList) {
      if (TIME_OFF_ALL_WOP_TYPES.includes(timeOffRequest.timeOffType)) {
        const response = await fetchLatestTimeOffRequestWOPFreezeEvent(
          station,
          employeeWorkgroup,
          timeOffRequest.shiftStartDateTime.toISODate(),
          timeOffRequest.timeOffType
        );
        if (response.status === 200) {
          const result = await response.json();
          if (result.latestWopFreezeEvent?.isFrozen) {
            isFrozen = true;
            frozenWopTypes.push(timeOffRequest.timeOffType);
            frozenRequestCount += 1;
          }
        }
      }
    }
    if (isFrozen) {
      isWopListFrozen.current = true;
      setWopFreezeMessage(buildWopFreezeMessage(frozenWopTypes, frozenRequestCount));
    } else {
      isWopListFrozen.current = false;
      setWopFreezeMessage("");
    }
  };

  return (
    <div className={`${classes.root}`} data-testid="employee-request-time-off-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>
          {props.isDesktopView ? (
            <div className={(isAddTimeOffDialogOpen || isEditTimeOffRequest) && classes.pointer}>
              <StarsBreadcrumbs
                breadcrumbsList={breadcrumbsList}
                breadcrumbsLinks={breadcrumbsLinks}
                isConfirmationRequired={confirmationRequired()}
                cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA}
              />
            </div>
          ) : (
            ""
          )}
          <div
            className={
              !props.isDesktopView
                ? classes.mobileRoot
                : `${classes.desktopRoot} ${(isAddTimeOffDialogOpen || isEditTimeOffRequest) && classes.pointer}`
            }
          >
            <div className={!props.isDesktopView ? `${classes.pageTitle} page-title-left` : `page-title-left`}>
              {" "}
              Request Time Off
            </div>
            <Divider className={!props.isDesktopView ? classes.divider : `divider ${classes.topDivider}`} />
            {stationList.length > 1 ? (
              <StarsSelectInput
                label="Station"
                menuMessage="Station"
                value={stationFilter}
                allOptions={stationList}
                sortComparator={sortOnCaseSensitiveTexts}
                onChange={handleStationChange}
                onBlur={handleStationBlur}
                errors={stationErrors}
                className="select-field"
                multiple={false}
              />
            ) : (
              <TextField
                className={!!props.isDesktopView ? "text-field" : classes.stationTextField}
                label="Station"
                variant="outlined"
                value={station}
                disabled
              />
            )}
            <Divider className={!props.isDesktopView ? classes.divider : `divider ${classes.bottomDivider}`} />
            <div>
              <div className={innerHtmlWidth ? classes.subHeaderMobile : classes.subHeader}>
                <div className="sub-title">Time Off Requested</div>
                <Button
                  className={innerHtmlWidth ? classes.button : ""}
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    timeOffRequestsList?.length >= 10
                      ? handleWarningDialogBox("You cannot request more than 10 shifts on one form!")
                      : setIsAddTimeOffDialogOpen(!isAddTimeOffDialogOpen)
                  }
                >
                  <AddIcon className="button-icon" />
                  ADD
                </Button>
              </div>
              {props.isDesktopView && timeOffRequestsList.length > 0 ? (
                <TimeOffRequestTable
                  timeOffRequestsList={timeOffRequestsList}
                  handleOnEditTimeOffRequest={(timeOffRequest) => handleOnEditTimeOffRequest(timeOffRequest)}
                  TOR_validateShiftOneHourDeadline={TOR_validateShiftOneHourDeadline}
                  station={station}
                />
              ) : !props.isDesktopView && timeOffRequestsList.length > 0 ? (
                <TimeOffRequestTableMobile
                  timeOffRequestsList={timeOffRequestsList}
                  handleOnEditTimeOffRequest={(timeOffRequest) => handleOnEditTimeOffRequest(timeOffRequest)}
                  isDesktopView={props.isDesktopView}
                />
              ) : (
                noTimeOffRequestMessage()
              )}
            </div>
            <div className={!props.isDesktopView ? "" : classes.actionButtons}>
              <Box m={1}>
                <Button
                  className="button"
                  variant="outlined"
                  color="primary"
                  size="large"
                  fullWidth={true}
                  onClick={() => toggleAreYouSureDialog(true)}
                >
                  <Typography variant="h5" component="h2">
                    <Box className="order-selection-text">Cancel</Box>
                  </Typography>
                </Button>
              </Box>

              <Box m={1}>
                <Button
                  className="button"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth={true}
                  onClick={async () => {
                    await setIsWopListFrozen(timeOffRequestsList);
                    if (timeOffRequestsList.length > 0) {
                      if (handleOnSubmitValidation()) {
                        toggleAreYouSureDialog(true);
                        setIsSubmitButtonClicked(true);
                      }
                    } else {
                      handleWarningDialogBox("You must add at least 1 shift!");
                    }
                  }}
                  disabled={isTimeOffRequestSubmitted}
                >
                  {isTimeOffRequestSubmitted ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Typography variant="h5" component="h2">
                      <Box className="order-selection-text">Submit</Box>
                    </Typography>
                  )}
                </Button>
              </Box>
            </div>
            {(isEditTimeOffRequest || isAddTimeOffDialogOpen) && (
              <AddTimeOffRequestDialog
                isAdmin={userSession.isAdmin}
                employeeWorkgroup={employeeWorkgroup}
                employeeJobCode={userSession.jobCode}
                isCreateTimeOff={false}
                station={station}
                isDialogOpen={isEditTimeOffRequest ? isEditTimeOffRequest : isAddTimeOffDialogOpen}
                closeAddTimeOff={() => handleCloseAddRequestModal()}
                handleChangeTimeOffTypes={selectTimeOffRequestTimeOff}
                selectedTimeOffType={timeOffRequestObj.current.timeOffType}
                handleOnSave={() => handleOnSave()}
                timeOffTypeError={timeOffTypeError}
                handleShiftStartDateChange={handleShiftStartDateChange}
                handleShiftStartDateHoursChange={handleShiftStartDateHoursChange}
                handleShiftStartDateMinChange={handleShiftStartDateMinChange}
                handleShiftEndDateChange={handleShiftEndDateChange}
                handleShiftEndDateHoursChange={handleShiftEndDateHoursChange}
                handleShiftEndDateMinChange={handleShiftEndDateMinChange}
                shiftStartDate={timeOffRequestObj.current.shiftStartDate}
                shiftStartDateError={startDateError}
                shiftStartDateHours={shiftStartTime.current.shiftStartHour}
                shiftStartDateMin={shiftStartTime.current.shiftStartMin}
                shiftStartTimeError={startTimeError}
                shiftEndDate={timeOffRequestObj.current.shiftEndDate}
                shiftEndDateError={endDateError}
                shiftEndDateHours={shiftEndTime.current.shiftEndHour}
                shiftEndDateMin={shiftEndTime.current.shiftEndMin}
                shiftEndDateTimeError={endTimeError}
                isEdit={isEditTimeOffRequest}
                handleOnRemove={() => {
                  toggleAreYouSureDialog(true);
                }}
                isAdminPage={isAdminPage}
                isDesktopView={props.isDesktopView}
                isDSTWarningOpen={isDSTWarningOpen}
                asSoonAsPossibleChecked={asSoonAsPossibleChecked}
                handleAsSoonAsPossibleChecked={handleAsSoonAsPossibleChecked}
                preferredWOPStartHour={preferredWOPStartTime.current.preferredWOPStartHour}
                preferredWOPStartMin={preferredWOPStartTime.current.preferredWOPStartMin}
                preferredWOPStartTimeError={preferredWOPStartTimeError}
                preferredWOPEndHour={preferredWOPEndTime.current.preferredWOPEndHour}
                preferredWOPEndMin={preferredWOPEndTime.current.preferredWOPEndMin}
                preferredWOPEndTimeError={preferredWOPEndTimeError}
                handlePreferredWOPStartHourChange={handlePreferredWOPStartHourChange}
                handlePreferredWOPStartMinChange={handlePreferredWOPStartMinChange}
                handlePreferredWOPEndHourChange={handlePreferredWOPEndHourChange}
                handlePreferredWOPEndMinChange={handlePreferredWOPEndMinChange}
                phoneNumber={phoneNumber}
                handlePhoneNumberChange={handlePhoneNumberChange}
                handlePhoneNumberBlur={handlePhoneNumberBlur}
                phoneNumberErrors={phoneNumberErrors}
              />
            )}
            {warningDialogBox && (
              <WarningDialog
                isDialogOpen={warningDialogBox}
                handleClickDialogClose={handleWarningDialogClose}
                header="Warning"
                message={warningDialogBoxMessage}
                severity={"error"}
              />
            )}
          </div>

          {isAreYouDialogOpen && renderCancelAreYouSureDialog()}
          {openConfirmationDialog && (
            <SuccessDialog
              isSuccessDialogOpen={openConfirmationDialog}
              title={TIME_OFF_CONFIRMATION_TITLE}
              message={
                timeOffRequestsList.length > 1
                  ? TOR_MULTIPLE_REQUESTS_SUBMITTED_MESSAGE
                  : TOR_SINGLE_REQUEST_SUBMITTED_MESSAGE
              }
              redirectTo={props.featureConfig.path}
            />
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeRequestTimeOff;
